<script setup>
// ============================================================================
// BonBoZ: thanh 24 giờ — mỗi THIẾT BỊ một thanh, mỗi ngày một hàng. Trả lời:
// khung giờ nào máy nhận được thông báo, khung giờ nào hụt, hụt bao nhiêu và
// đã được gửi lại tới máy chưa.
//
//   xanh   tới máy ngay lượt gửi đầu
//   vàng   lượt đầu hụt, gửi lại thì tới
//   đỏ     không tới, kể cả sau khi gửi lại (gồm cả không gửi vì máy hỏng)
//   xám    30 phút không có tin nào: không có gì để biết
//
// Hai máy của cùng một người tách thanh riêng: điện thoại có thể mất tin
// trong khi laptop vẫn nhận. Tin nhân viên tắt trong cài đặt và tin không có
// máy nào để gửi không thuộc máy nào, chỉ ghi một dòng dưới tên.
//
// Lọc theo loại máy và gom theo ngày / theo nhân viên đều làm ở đây, không gọi
// lại server. Cách tính đoạn màu ở `pushTimeline.js`.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionCard from './SectionCard.vue';
import {
  DAY_MINUTES,
  changeLabels,
  deviceInScope,
  hhmm,
  segmentsOf,
  summarize,
} from './pushTimeline';

const props = defineProps({
  // `timeline` của server: { days: ['2026-10-20', ...], agents: [...] }.
  timeline: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const { t, locale } = useI18n();

const HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const GRID_HOURS = [3, 6, 9, 12, 15, 18, 21];
const MOBILE_HOURS = [0, 6, 12, 18, 24];
const COLOR_CLASS = {
  ok: 'bg-n-teal-9',
  resent: 'bg-n-amber-9',
  lost: 'bg-n-ruby-9',
  empty: 'bg-n-slate-4',
};
const DEVICE_SCOPES = {
  phone: 'BONBOZ.PUSH_REPORT.TIMELINE.SCOPE_PHONE',
  desktop: 'BONBOZ.PUSH_REPORT.TIMELINE.SCOPE_DESKTOP',
  all: 'BONBOZ.PUSH_REPORT.TIMELINE.SCOPE_ALL',
};
const GROUP_BY = {
  day: 'BONBOZ.PUSH_REPORT.TIMELINE.GROUP_DAY',
  agent: 'BONBOZ.PUSH_REPORT.TIMELINE.GROUP_AGENT',
};
const DEVICE_TYPE_KEYS = {
  phone: 'BONBOZ.PUSH_REPORT.TIMELINE.DEVICE_PHONE',
  desktop: 'BONBOZ.PUSH_REPORT.TIMELINE.DEVICE_DESKTOP',
  unknown: 'BONBOZ.PUSH_REPORT.TIMELINE.DEVICE_UNKNOWN',
};
// Thứ tự liệt kê trong dòng chi tiết.
const OUTCOME_KEYS = {
  ok: 'BONBOZ.PUSH_REPORT.TIMELINE.OUTCOME.OK',
  resent: 'BONBOZ.PUSH_REPORT.TIMELINE.OUTCOME.RESENT',
  rejected: 'BONBOZ.PUSH_REPORT.TIMELINE.OUTCOME.REJECTED',
  no_ack: 'BONBOZ.PUSH_REPORT.TIMELINE.OUTCOME.NO_ACK',
  not_sent: 'BONBOZ.PUSH_REPORT.TIMELINE.OUTCOME.NOT_SENT',
};

// Dùng chung với sơ đồ đường đi (PushReport.vue giữ giá trị).
const deviceScope = defineModel('deviceScope', {
  type: String,
  default: 'phone',
});
const groupBy = ref('day');

const percentOf = minute => `${(minute / DAY_MINUTES) * 100}%`;

const dayLabel = day =>
  new Date(`${day}T00:00:00`).toLocaleDateString(locale.value, {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  });

const inScope = device => deviceInScope(device, deviceScope.value);

const deviceText = device => {
  if (device.deleted) return t('BONBOZ.PUSH_REPORT.TIMELINE.DEVICE_DELETED');
  const parts = [
    t(DEVICE_TYPE_KEYS[device.type] || DEVICE_TYPE_KEYS.unknown),
    device.label,
  ].filter(Boolean);
  const text = parts.join(' · ');
  return device.inactive
    ? `${text} ${t('BONBOZ.PUSH_REPORT.TIMELINE.DEVICE_INACTIVE')}`
    : text;
};

// Các thanh của một người trong một ngày. Người không có máy nào trong phạm
// vi vẫn có một hàng (thanh xám) để không biến mất khỏi danh sách.
const rowsOf = (agent, day) => {
  const devices = (agent.devices || []).filter(inScope);
  const personNotes = {
    off: agent.off?.[day] || 0,
    noDevice: agent.no_device?.[day] || 0,
  };
  if (!devices.length) {
    return [
      {
        key: `${day}-${agent.user_id}-none`,
        agent,
        day,
        device: null,
        notes: personNotes,
        segments: segmentsOf([]),
        labels: [],
        summary: summarize([]),
      },
    ];
  }
  return devices.map((device, index) => {
    const events = device.days?.[day] || [];
    const segments = segmentsOf(events);
    return {
      key: `${day}-${agent.user_id}-${device.id}`,
      agent,
      day,
      device,
      // Ghi chú theo người chỉ đặt một lần, dưới máy đầu tiên.
      notes: index === 0 ? personNotes : { off: 0, noDevice: 0 },
      segments,
      labels: changeLabels(segments),
      summary: summarize(events),
    };
  });
};

const totalsOf = rows =>
  rows.reduce(
    (sum, row) => ({
      missed: sum.missed + row.summary.missed,
      resent: sum.resent + row.summary.resent,
      lost: sum.lost + row.summary.lost,
    }),
    { missed: 0, resent: 0, lost: 0 }
  );

const groups = computed(() => {
  const days = props.timeline?.days || [];
  const agents = props.timeline?.agents || [];
  const list =
    groupBy.value === 'day'
      ? days.map(day => ({
          key: `day-${day}`,
          title: dayLabel(day),
          rows: agents.flatMap(agent => rowsOf(agent, day)),
        }))
      : agents.map(agent => ({
          key: `agent-${agent.user_id}`,
          title: agent.name,
          rows: days.flatMap(day => rowsOf(agent, day)),
        }));
  return list.map(group => ({ ...group, totals: totalsOf(group.rows) }));
});

// Mở nhóm đầu tiên (ngày gần nhất / người đầu tiên), thu gọn phần còn lại;
// tiêu đề nhóm thu gọn vẫn có tóm tắt nên lướt được.
const collapsed = ref(new Set());
const resetCollapsed = () => {
  collapsed.value = new Set(groups.value.slice(1).map(group => group.key));
};
watch(
  () => [groupBy.value, props.timeline],
  () => resetCollapsed(),
  { immediate: true }
);

const toggle = key => {
  const next = new Set(collapsed.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsed.value = next;
};
const collapseAll = () => {
  collapsed.value = new Set(groups.value.map(group => group.key));
};
const expandAll = () => {
  collapsed.value = new Set();
};

// Cột đầu: nhóm theo ngày thì ghi tên người, nhóm theo người thì ghi ngày.
const rowTitle = row =>
  groupBy.value === 'day' ? row.agent.name : dayLabel(row.day);

const describe = (segment, row) => {
  const name = row.device
    ? `${rowTitle(row)} · ${deviceText(row.device)}`
    : rowTitle(row);
  const range = `${hhmm(segment.start)}–${hhmm(segment.end)}`;
  if (segment.color === 'empty') {
    return t('BONBOZ.PUSH_REPORT.TIMELINE.DETAIL_EMPTY', { name, range });
  }
  const parts = Object.entries(OUTCOME_KEYS)
    .map(([outcome, key]) => [
      key,
      segment.events.filter(e => e.outcome === outcome).length,
    ])
    .filter(([, count]) => count)
    .map(([key, count]) =>
      t('BONBOZ.PUSH_REPORT.TIMELINE.DETAIL_PART', { count, what: t(key) })
    )
    .join(', ');
  return t('BONBOZ.PUSH_REPORT.TIMELINE.DETAIL', { name, range, parts });
};

const detail = ref('');
const show = (segment, row) => {
  detail.value = describe(segment, row);
};

const pillClass = active =>
  active
    ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1';
</script>

<template>
  <SectionCard
    :title="$t('BONBOZ.PUSH_REPORT.TIMELINE.TITLE')"
    :description="$t('BONBOZ.PUSH_REPORT.TIMELINE.DESCRIPTION')"
  >
    <div class="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3 text-xs">
      <div class="flex items-center gap-2">
        <span class="text-n-slate-11">
          {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.SCOPE_LABEL') }}
        </span>
        <div class="flex gap-1">
          <button
            v-for="(key, scope) in DEVICE_SCOPES"
            :key="scope"
            type="button"
            class="px-2 py-1 border rounded-md"
            :class="pillClass(scope === deviceScope)"
            :data-testid="`push-timeline-scope-${scope}`"
            @click="deviceScope = scope"
          >
            {{ $t(key) }}
          </button>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-n-slate-11">
          {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.GROUP_LABEL') }}
        </span>
        <div class="flex gap-1">
          <button
            v-for="(key, mode) in GROUP_BY"
            :key="mode"
            type="button"
            class="px-2 py-1 border rounded-md"
            :class="pillClass(mode === groupBy)"
            :data-testid="`push-timeline-group-${mode}`"
            @click="groupBy = mode"
          >
            {{ $t(key) }}
          </button>
        </div>
      </div>
      <div class="flex gap-3 ms-auto">
        <button
          type="button"
          class="font-medium text-n-brand hover:underline"
          data-testid="push-timeline-collapse-all"
          @click="collapseAll"
        >
          {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.COLLAPSE_ALL') }}
        </button>
        <button
          type="button"
          class="font-medium text-n-brand hover:underline"
          data-testid="push-timeline-expand-all"
          @click="expandAll"
        >
          {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.EXPAND_ALL') }}
        </button>
      </div>
    </div>

    <div
      class="grid gap-x-5 gap-y-1.5 mb-4 text-xs sm:grid-cols-2 text-n-slate-12"
      data-testid="push-timeline-legend"
    >
      <span class="flex items-start gap-2">
        <span class="w-6 h-3 mt-0.5 rounded-sm shrink-0 bg-n-teal-9" />
        {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.LEGEND_OK') }}
      </span>
      <span class="flex items-start gap-2">
        <span class="w-6 h-3 mt-0.5 rounded-sm shrink-0 bg-n-amber-9" />
        {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.LEGEND_RESENT') }}
      </span>
      <span class="flex items-start gap-2">
        <span class="w-6 h-3 mt-0.5 rounded-sm shrink-0 bg-n-ruby-9" />
        {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.LEGEND_LOST') }}
      </span>
      <span class="flex items-start gap-2">
        <span class="w-6 h-3 mt-0.5 rounded-sm shrink-0 bg-n-slate-4" />
        {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.LEGEND_EMPTY') }}
      </span>
    </div>

    <div v-if="loading" class="h-40 rounded-lg bg-n-alpha-2 animate-pulse" />
    <p v-else-if="!groups.length" class="m-0 text-sm text-n-slate-11">
      {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-3" data-testid="push-timeline">
      <!-- Trục giờ dùng chung, đặt một lần ở đầu. -->
      <div
        class="hidden px-3 border border-transparent md:grid md:grid-cols-[10rem_minmax(0,1fr)] xl:grid-cols-[11rem_minmax(0,1fr)_18rem] gap-x-4"
        aria-hidden="true"
      >
        <span />
        <div class="relative h-4 text-[11px] tabular-nums text-n-slate-11">
          <span
            v-for="hour in HOURS"
            :key="hour"
            class="absolute"
            :class="{
              '-translate-x-1/2': hour > 0 && hour < 24,
              '-translate-x-full': hour === 24,
            }"
            :style="{ left: percentOf(hour * 60) }"
          >
            {{ `${hour}h` }}
          </span>
        </div>
        <span class="hidden xl:block" />
      </div>

      <section
        v-for="group in groups"
        :key="group.key"
        class="flex flex-col border rounded-lg border-n-weak"
        :data-testid="`push-timeline-${group.key}`"
      >
        <button
          type="button"
          class="flex flex-wrap items-center w-full gap-x-3 gap-y-1 px-3 py-2 text-left rounded-lg hover:bg-n-alpha-1"
          :aria-expanded="!collapsed.has(group.key)"
          :data-testid="`push-timeline-toggle-${group.key}`"
          @click="toggle(group.key)"
        >
          <span
            class="size-4 shrink-0 text-n-slate-11"
            :class="
              collapsed.has(group.key)
                ? 'i-lucide-chevron-right'
                : 'i-lucide-chevron-down'
            "
            aria-hidden="true"
          />
          <span class="text-sm font-semibold capitalize text-n-slate-12">
            {{ group.title }}
          </span>
          <span class="text-xs tabular-nums text-n-slate-11">
            <template v-if="group.totals.missed">
              {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.GROUP_TOTALS', group.totals) }}
            </template>
            <template v-else>
              {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.GROUP_NO_MISS') }}
            </template>
          </span>
        </button>

        <div v-if="!collapsed.has(group.key)" class="px-3 pb-2">
          <!-- Màn hình hẹp không có trục giờ chung ở trên: mỗi nhóm một trục gọn. -->
          <div
            class="relative h-4 mb-1 text-[10px] tabular-nums md:hidden text-n-slate-11"
            aria-hidden="true"
          >
            <span
              v-for="hour in MOBILE_HOURS"
              :key="hour"
              class="absolute"
              :class="{
                '-translate-x-1/2': hour > 0 && hour < 24,
                '-translate-x-full': hour === 24,
              }"
              :style="{ left: percentOf(hour * 60) }"
            >
              {{ `${hour}h` }}
            </span>
          </div>
          <div
            v-for="row in group.rows"
            :key="row.key"
            class="grid grid-cols-1 md:grid-cols-[10rem_minmax(0,1fr)] xl:grid-cols-[11rem_minmax(0,1fr)_18rem] gap-x-4 gap-y-1 py-2 border-t border-n-weak"
            :data-testid="`push-timeline-row-${row.key}`"
          >
            <div class="min-w-0 text-sm md:pt-6">
              <span
                class="block font-medium capitalize truncate text-n-slate-12"
              >
                {{ rowTitle(row) }}
              </span>
              <span
                class="block text-[11px] truncate text-n-slate-11"
                data-testid="push-timeline-device"
              >
                {{
                  row.device
                    ? deviceText(row.device)
                    : $t('BONBOZ.PUSH_REPORT.TIMELINE.NO_DEVICE_IN_SCOPE')
                }}
              </span>
              <span
                v-if="row.notes.off"
                class="block text-[11px] text-n-amber-11"
                data-testid="push-timeline-off"
              >
                {{
                  $t('BONBOZ.PUSH_REPORT.TIMELINE.OFF', {
                    count: row.notes.off,
                  })
                }}
              </span>
              <span
                v-if="row.notes.noDevice"
                class="block text-[11px] text-n-ruby-11"
                data-testid="push-timeline-no-device"
              >
                {{
                  $t('BONBOZ.PUSH_REPORT.TIMELINE.NO_DEVICE_NOT_SENT', {
                    count: row.notes.noDevice,
                  })
                }}
              </span>
            </div>

            <div class="relative min-w-0 pt-7">
              <div class="absolute inset-x-0 top-0 h-7" aria-hidden="true">
                <span
                  v-for="label in row.labels"
                  :key="label.minute"
                  class="absolute text-[10px] leading-none tabular-nums -translate-x-1/2 text-n-slate-12 whitespace-nowrap"
                  :class="label.row ? 'top-0' : 'top-3'"
                  :style="{ left: percentOf(label.minute) }"
                >
                  {{ hhmm(label.minute) }}
                  <span
                    class="absolute w-px left-1/2 top-2.5 bg-n-slate-9"
                    :class="label.row ? 'h-[1.125rem]' : 'h-1.5'"
                  />
                </span>
              </div>
              <div class="relative h-6 overflow-hidden rounded bg-n-slate-4">
                <button
                  v-for="segment in row.segments"
                  :key="segment.start"
                  type="button"
                  class="absolute inset-y-0 p-0 border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-slate-12"
                  :class="COLOR_CLASS[segment.color]"
                  :style="{
                    left: percentOf(segment.start),
                    width: percentOf(segment.end - segment.start),
                  }"
                  :title="describe(segment, row)"
                  :aria-label="describe(segment, row)"
                  :data-color="segment.color"
                  @click="show(segment, row)"
                />
                <span
                  v-for="hour in GRID_HOURS"
                  :key="hour"
                  class="absolute inset-y-0 w-px pointer-events-none bg-n-slate-12/10"
                  :style="{ left: percentOf(hour * 60) }"
                />
              </div>
            </div>

            <div
              class="flex flex-col gap-0.5 text-xs tabular-nums md:col-start-2 xl:col-start-auto xl:pt-6 text-n-slate-12"
              data-testid="push-timeline-summary"
            >
              <span v-if="!row.summary.total" class="text-n-slate-11">
                {{ $t('BONBOZ.PUSH_REPORT.TIMELINE.NO_MESSAGES') }}
              </span>
              <span v-else-if="!row.summary.missed">
                {{
                  $t('BONBOZ.PUSH_REPORT.TIMELINE.ALL_OK', {
                    count: row.summary.total,
                  })
                }}
              </span>
              <template v-else>
                <span>
                  {{
                    $t('BONBOZ.PUSH_REPORT.TIMELINE.TOTAL', {
                      count: row.summary.total,
                      ok: row.summary.ok,
                    })
                  }}
                </span>
                <span class="font-medium">
                  {{
                    $t('BONBOZ.PUSH_REPORT.TIMELINE.MISSED', {
                      count: row.summary.missed,
                      ranges: row.summary.missedRanges,
                    })
                  }}
                </span>
                <span class="text-n-amber-11">
                  {{
                    row.summary.resent
                      ? $t('BONBOZ.PUSH_REPORT.TIMELINE.RESENT_IN', {
                          count: row.summary.resent,
                          ranges: row.summary.resentRanges,
                        })
                      : $t('BONBOZ.PUSH_REPORT.TIMELINE.RESENT', { count: 0 })
                  }}
                </span>
                <span class="text-n-ruby-11">
                  {{
                    row.summary.lost
                      ? $t('BONBOZ.PUSH_REPORT.TIMELINE.LOST_IN', {
                          count: row.summary.lost,
                          ranges: row.summary.lostRanges,
                        })
                      : $t('BONBOZ.PUSH_REPORT.TIMELINE.LOST', { count: 0 })
                  }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </section>

      <p
        class="px-3 py-2 m-0 text-xs border border-dashed rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
        aria-live="polite"
        data-testid="push-timeline-detail"
      >
        {{ detail || $t('BONBOZ.PUSH_REPORT.TIMELINE.DETAIL_HINT') }}
      </p>
    </div>
  </SectionCard>
</template>
